import React from "react";
import { Box, Typography, Grid, Paper, Stack } from "@mui/material";

import {
  KickOffReturnPlayCall,
  KickOffReturnPlayLog,
  PenaltyLog,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { PenaltyLogDetails } from "../Components/PenaltyLogDetails";

interface KickOffReturnLogDetailsProps {
  playCall: KickOffReturnPlayCall;
  playLog: KickOffReturnPlayLog;
  penalties: PenaltyLog[];
}

export const KickOffReturnLogDetails: React.FC<
  KickOffReturnLogDetailsProps
> = ({ playCall, playLog, penalties }) => {
  
  const calculateGridSize = () => {
    if (penalties.length > 0) {
      return 4;
    }
    return 6;
  }
  
  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {playCall.personnel_group.name || ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Play Call
                </Typography>
                <Typography variant="h6">{playCall.name || ""}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            {playLog && (
              <Stack spacing={2}>
                {/* Returner */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Returner
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {jerseyNumberNameDisplay(playLog.returner_roster_player)}
                  </Typography>
                </Box>

                {/* Kickoff Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Kickoff Details
                  </Typography>
                  <Typography variant="body1">
                    From: {playLog.kickoff_from} yard line
                  </Typography>
                  <Typography variant="body1">
                    To: {playLog.kickoff_to} yard line
                  </Typography>
                  <Typography variant="body1">
                    Kickoff Distance: {playLog.kickoff_yardage} yards
                  </Typography>
                </Box>

                {/* Return Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Return Details
                  </Typography>
                  <Typography variant="body1">
                    Returned To: {playLog.return_to_yardline} yard line
                  </Typography>
                  <Typography
                    variant="h5"
                    color={
                      playLog.return_yardage > 0 ? "success.main" : "error.main"
                    }
                    sx={{ mt: 1 }}
                  >
                    {playLog.return_yardage > 0 ? "+" : ""}
                    {playLog.return_yardage} return yards
                  </Typography>
                </Box>

                {/* Fumble Section */}
                {playLog.fumble && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      FUMBLE
                    </Typography>
                    {playLog.fumble.fumble_roster_player && (
                      <Typography variant="body2">
                        Fumbled by:{" "}
                        {jerseyNumberNameDisplay(
                          playLog.fumble.fumble_roster_player
                        )}
                      </Typography>
                    )}
                    {playLog.fumble.fumble_recovery_roster_player && (
                      <Typography variant="body2">
                        Recovered by:{" "}
                        {jerseyNumberNameDisplay(
                          playLog.fumble.fumble_recovery_roster_player
                        )}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Penalties Section */}
        <Grid item xs={12} md={4}>
          <PenaltyLogDetails penalties={penalties} />
        </Grid>
      </Grid>
    </Box>
  );
};
